import { useMemo } from "react";

import { mean } from "lodash";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { CurrencyInput, PercentInput, Select } from "@components/v2";
import { formatting } from "@north-beam/nb-common";

import { useUniversalBenchmarksConfiguratorContext } from "../../hooks";
import { type MetricPointWithDate } from "../../types";
import {
  adjustMetricsGrossRev,
  calculateBaselineDaysAverages,
  roundNumberTwoDecimalPlaces,
  sumCogs,
  sumContributionMargin,
} from "../../utils";

import { FormRow } from "./form-row";

export const AdjustForProfitability = () => {
  const { user } = useUser();
  const {
    baselineDays,
    cancelledOrders,
    baselineMetrics,
    baselineFilters,
    setBaselineFilters,
  } = useUniversalBenchmarksConfiguratorContext();

  const aov = useMemo(() => {
    const gross = adjustMetricsGrossRev(
      baselineDays,
      user.ecomAccountingSettings,
    ) as unknown as MetricPointWithDate[];
    let aovMetric = baselineMetrics.aov;

    if (baselineFilters.profitableDayThreshold.method === "first-time") {
      aovMetric = baselineMetrics.ftAov;
    }

    if (baselineFilters.profitableDayThreshold.method === "returning") {
      aovMetric = baselineMetrics.rtnAov;
    }

    return mean(gross.map((row) => aovMetric.calculate(row)));
  }, [
    user,
    baselineMetrics,
    baselineDays,
    baselineFilters.profitableDayThreshold.method,
  ]);

  const cogs = baselineFilters.cogs;
  const aovNet =
    aov -
    cogs.shipping.value -
    cogs.taxes.value -
    cogs.discounts.value -
    cogs.returns.value;

  const formatCurrency = (value: number) => {
    return formatting.formatCurrency.exact(value, {
      code: user.currencies.currentCurrency,
    });
  };

  const formatPercentage = (value: number) => {
    return formatting.formatPercentage(value / 100, {
      precision: "exact",
    });
  };

  return (
    <div className="py-6 pl-6 pr-3">
      <h3 className="text-base font-semibold mb-1">Adjust for profitability</h3>
      <p className="text-sm text-v2-secondary">
        Identifying profitable days means unprofitable days can be excluded from
        the calculation.
      </p>
      <div>
        <h4 className="text-sm font-bold">Define profitable days</h4>
        <div className="flex flex-wrap items-center gap-2 border-b border-v2-tertiary pb-4 mb-4">
          <Select
            buttonClassName="w-[240px]"
            options={[
              {
                value: "is-greater-or-equal-to-costs",
                label: "Breakeven",
              },
              {
                value: "contribution-margin-is-at-least",
                label: "Contribution margin is at least",
              },
              {
                value: "generate-at-least-in-profit",
                label: "Earn a minimum of",
              },
            ]}
            value={baselineFilters.profitableDayThreshold.equation}
            onChange={(equation) => {
              if (equation) {
                setBaselineFilters({
                  ...baselineFilters,
                  profitableDayThreshold: {
                    ...baselineFilters.profitableDayThreshold,
                    equation,
                    method: "blended",
                    value: 0,
                  },
                });
              }
            }}
          />
          {baselineFilters.profitableDayThreshold.equation ===
            "contribution-margin-is-at-least" && (
            <PercentInput
              className="w-[60px]"
              defaultValue={0}
              value={baselineFilters.profitableDayThreshold.value}
              onChange={(value) => {
                if (value !== undefined) {
                  setBaselineFilters({
                    ...baselineFilters,
                    profitableDayThreshold: {
                      ...baselineFilters.profitableDayThreshold,
                      value,
                    },
                  });
                }
              }}
            />
          )}
          {baselineFilters.profitableDayThreshold.equation ===
            "generate-at-least-in-profit" && (
            <CurrencyInput
              className="w-[80px]"
              defaultValue={0}
              value={baselineFilters.profitableDayThreshold.value}
              onChange={(value) => {
                if (value !== undefined) {
                  setBaselineFilters({
                    ...baselineFilters,
                    profitableDayThreshold: {
                      ...baselineFilters.profitableDayThreshold,
                      value,
                    },
                  });
                }
              }}
            />
          )}
          {baselineFilters.profitableDayThreshold.equation ===
          "generate-at-least-in-profit" ? (
            <div>per day on</div>
          ) : (
            <div>on</div>
          )}
          <Select
            buttonClassName="w-[210px]"
            disabled={
              baselineFilters.profitableDayThreshold.equation ===
              "contribution-margin-is-at-least"
            }
            options={[
              { value: "blended", label: "all orders" },
              { value: "first-time", label: "first-time customer orders" },
              { value: "returning", label: "returning customer orders" },
            ]}
            value={baselineFilters.profitableDayThreshold.method}
            onChange={(method) => {
              if (method) {
                const {
                  averageSpend,
                  averageDiscount,
                  averageShipping,
                  averageTaxes,
                  averageRefunds,
                  averageCancellations,
                } = calculateBaselineDaysAverages({
                  baselineDays,
                  cancelledOrders,
                  baselineMetrics,
                  ecomAccountingSettings: user.ecomAccountingSettings,
                  profitableDayThresholdMethod: method,
                });

                setBaselineFilters({
                  ...baselineFilters,
                  cogs: {
                    ...baselineFilters.cogs,
                    spend: {
                      ...baselineFilters.cogs.spend,
                      value: roundNumberTwoDecimalPlaces(averageSpend),
                    },
                    discounts: {
                      ...baselineFilters.cogs.discounts,
                      value: roundNumberTwoDecimalPlaces(averageDiscount),
                    },
                    shipping: {
                      ...baselineFilters.cogs.shipping,
                      value: roundNumberTwoDecimalPlaces(averageShipping),
                    },
                    taxes: {
                      ...baselineFilters.cogs.taxes,
                      value: roundNumberTwoDecimalPlaces(averageTaxes),
                    },
                    returns: {
                      ...baselineFilters.cogs.returns,
                      value: roundNumberTwoDecimalPlaces(
                        averageRefunds + averageCancellations,
                      ),
                    },
                  },
                  adjustment: null,
                  profitableDayThreshold: {
                    ...baselineFilters.profitableDayThreshold,
                    method,
                  },
                });
              }
            }}
          />
        </div>
      </div>
      <div className="space-y-4 border-b border-v2-tertiary pb-4 mb-4">
        <div>
          <h4 className="text-sm font-bold mb-2">Define costs per order</h4>
          <p className="text-sm text-v2-secondary">
            Add your average COGS to accurately calculate profit margins.
            Northbeam automatically fills costs based on the average from your
            selected days.
          </p>
        </div>
        <FormRow
          aov={aov}
          title="Media Spend"
          selected={baselineFilters.cogs.spend.selected}
          value={baselineFilters.cogs.spend.value}
          onUpdateSelected={(selected) => {
            setBaselineFilters({
              ...baselineFilters,
              cogs: {
                ...baselineFilters.cogs,
                spend: {
                  ...baselineFilters.cogs.spend,
                  selected,
                },
              },
            });
          }}
          onUpdateValue={(value) => {
            setBaselineFilters({
              ...baselineFilters,
              cogs: {
                ...baselineFilters.cogs,
                spend: {
                  ...baselineFilters.cogs.spend,
                  value,
                },
              },
            });
          }}
        />
        <FormRow
          aov={aov}
          title="Other Marketing Costs"
          tooltipText={
            <span>
              <span className="font-bold">Other Marketing Costs:</span>{" "}
              Additional marketing costs such as platform fees or agency
              <br />
              fees you would like to take into account.
            </span>
          }
          selected={baselineFilters.cogs.otherMarketingCosts.selected}
          value={baselineFilters.cogs.otherMarketingCosts.value}
          onUpdateSelected={(selected) => {
            setBaselineFilters({
              ...baselineFilters,
              cogs: {
                ...baselineFilters.cogs,
                otherMarketingCosts: {
                  ...baselineFilters.cogs.otherMarketingCosts,
                  selected,
                },
              },
            });
          }}
          onUpdateValue={(value) => {
            setBaselineFilters({
              ...baselineFilters,
              cogs: {
                ...baselineFilters.cogs,
                otherMarketingCosts: {
                  ...baselineFilters.cogs.otherMarketingCosts,
                  value,
                },
              },
            });
          }}
        />
        <FormRow
          aov={aov}
          title="Taxes"
          selected={baselineFilters.cogs.taxes.selected}
          value={baselineFilters.cogs.taxes.value}
          onUpdateSelected={(selected) => {
            setBaselineFilters({
              ...baselineFilters,
              cogs: {
                ...baselineFilters.cogs,
                taxes: {
                  ...baselineFilters.cogs.taxes,
                  selected,
                },
              },
            });
          }}
          onUpdateValue={(value) => {
            setBaselineFilters({
              ...baselineFilters,
              cogs: {
                ...baselineFilters.cogs,
                taxes: {
                  ...baselineFilters.cogs.taxes,
                  value,
                },
              },
            });
          }}
        />
        <FormRow
          aov={aov}
          title="Shipping"
          selected={baselineFilters.cogs.shipping.selected}
          value={baselineFilters.cogs.shipping.value}
          onUpdateSelected={(selected) => {
            setBaselineFilters({
              ...baselineFilters,
              cogs: {
                ...baselineFilters.cogs,
                shipping: {
                  ...baselineFilters.cogs.shipping,
                  selected,
                },
              },
            });
          }}
          onUpdateValue={(value) => {
            setBaselineFilters({
              ...baselineFilters,
              cogs: {
                ...baselineFilters.cogs,
                shipping: {
                  ...baselineFilters.cogs.shipping,
                  value,
                },
              },
            });
          }}
        />
        <FormRow
          aov={aov}
          title="Discounts"
          selected={baselineFilters.cogs.discounts.selected}
          value={baselineFilters.cogs.discounts.value}
          onUpdateSelected={(selected) => {
            setBaselineFilters({
              ...baselineFilters,
              cogs: {
                ...baselineFilters.cogs,
                discounts: {
                  ...baselineFilters.cogs.discounts,
                  selected,
                },
              },
            });
          }}
          onUpdateValue={(value) => {
            setBaselineFilters({
              ...baselineFilters,
              cogs: {
                ...baselineFilters.cogs,
                discounts: {
                  ...baselineFilters.cogs.discounts,
                  value,
                },
              },
            });
          }}
        />
        <FormRow
          aov={aov}
          title="Return rate"
          selected={baselineFilters.cogs.returns.selected}
          value={baselineFilters.cogs.returns.value}
          onUpdateSelected={(selected) => {
            setBaselineFilters({
              ...baselineFilters,
              cogs: {
                ...baselineFilters.cogs,
                returns: {
                  ...baselineFilters.cogs.returns,
                  selected,
                },
              },
            });
          }}
          onUpdateValue={(value) => {
            setBaselineFilters({
              ...baselineFilters,
              cogs: {
                ...baselineFilters.cogs,
                returns: {
                  ...baselineFilters.cogs.returns,
                  value,
                },
              },
            });
          }}
        />
        <FormRow
          aov={aov}
          title="Product Costs"
          selected={baselineFilters.cogs.productCosts.selected}
          value={baselineFilters.cogs.productCosts.value}
          onUpdateSelected={(selected) => {
            setBaselineFilters({
              ...baselineFilters,
              cogs: {
                ...baselineFilters.cogs,
                productCosts: {
                  ...baselineFilters.cogs.productCosts,
                  selected,
                },
              },
            });
          }}
          onUpdateValue={(value) => {
            setBaselineFilters({
              ...baselineFilters,
              cogs: {
                ...baselineFilters.cogs,
                productCosts: { ...baselineFilters.cogs.productCosts, value },
              },
            });
          }}
        />
        <FormRow
          aov={aov}
          title="All Other Costs"
          tooltipText={
            <span>
              <span className="font-bold">All Other Costs:</span> Any other
              overhead costs you'd like to remove from profit
              <br />
              such as warehousing and freight.
            </span>
          }
          selected={baselineFilters.cogs.otherCosts.selected}
          value={baselineFilters.cogs.otherCosts.value}
          onUpdateSelected={(selected) => {
            setBaselineFilters({
              ...baselineFilters,
              cogs: {
                ...baselineFilters.cogs,
                otherCosts: { ...baselineFilters.cogs.otherCosts, selected },
              },
            });
          }}
          onUpdateValue={(value) => {
            setBaselineFilters({
              ...baselineFilters,
              cogs: {
                ...baselineFilters.cogs,
                otherCosts: { ...baselineFilters.cogs.otherCosts, value },
              },
            });
          }}
        />
      </div>
      <div className="space-y-4">
        <div className="flex item-center gap-5">
          <div
            className={
              "shrink-0 w-[240px] font-semibold pr-2 flex items-center justify-end"
            }
          ></div>
          <div className="flex items-center">
            <div className="shrink-0 w-[70px] text-v2-secondary">
              {formatCurrency(aov)}
            </div>
            <div className="text-sm font-semibold">
              <GenericTooltip
                placement="top"
                content={
                  <span>
                    <span className="font-bold">Gross Revenue:</span> Total
                    sales before any deductions, including discounts and
                    returns.
                  </span>
                }
              >
                Gross Revenue
              </GenericTooltip>
            </div>
          </div>
        </div>
        <div className="flex item-center gap-5">
          <div
            className={
              "shrink-0 w-[240px] font-semibold pr-2 flex items-center justify-end"
            }
          ></div>
          <div className="flex items-center">
            <div className="shrink-0 w-[70px] text-v2-secondary">
              {formatCurrency(aovNet)}
            </div>
            <div className="text-sm font-semibold">
              <GenericTooltip
                placement="top"
                content={
                  <span>
                    <span className="font-bold">Net Revenue:</span> Revenue
                    after subtracting discounts, shipping, taxes, and returns
                    <br />
                    from gross revenue.
                  </span>
                }
              >
                Net Revenue
              </GenericTooltip>
            </div>
          </div>
        </div>
        <div className="flex item-center gap-5">
          <div
            className={
              "shrink-0 w-[240px] font-semibold pr-2 flex items-center justify-end"
            }
          >
            {formatPercentage((sumContributionMargin(cogs) / aovNet) * 100)}
          </div>
          <div className="flex items-center">
            <div className="shrink-0 w-[70px] text-v2-secondary">
              {formatCurrency(sumContributionMargin(cogs))}
            </div>
            <div className="text-sm font-semibold">
              <GenericTooltip
                placement="top"
                content={
                  <span>
                    <span className="font-bold">
                      Marketing Contribution Margin:
                    </span>{" "}
                    Net revenue minus media spend and other marketing costs,
                    <br />
                    indicating profitability before fixed costs.
                  </span>
                }
              >
                Marketing Contribution Margin
              </GenericTooltip>
            </div>
          </div>
        </div>
        <div className="flex item-center gap-5">
          <div
            className={
              "shrink-0 w-[240px] font-semibold pr-2 flex items-center justify-end"
            }
          >
            {formatPercentage((sumCogs(baselineFilters.cogs) / aov) * 100)}
          </div>
          <div className="flex items-center">
            <div className="shrink-0 w-[70px] text-v2-secondary">
              {formatCurrency(sumCogs(baselineFilters.cogs))}
            </div>
            <div className="text-sm font-semibold">
              <GenericTooltip
                placement="top"
                content={
                  <span>
                    <span className="font-bold">Total Costs:</span> Sum of all
                    expenses, including marketing, taxes, shipping, product
                    costs, and returns.
                  </span>
                }
              >
                Total costs
              </GenericTooltip>
            </div>
          </div>
        </div>
        <div className="flex item-center gap-5">
          <div
            className={
              "shrink-0 w-[240px] font-semibold pr-2 flex items-center justify-end"
            }
          >
            {formatPercentage(
              ((aov - sumCogs(baselineFilters.cogs)) / aov) * 100,
            )}
          </div>
          <div className="flex items-center">
            <div className="shrink-0 w-[70px] text-v2-secondary">
              {formatCurrency(aov - sumCogs(baselineFilters.cogs))}
            </div>
            <div className="text-sm font-semibold">
              <GenericTooltip
                placement="top"
                content={
                  <span>
                    <span className="font-bold">Profit:</span> The final amount
                    remaining after all costs are deducted from net revenue.
                  </span>
                }
              >
                Profit
              </GenericTooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
